/* global google */
import $ from "jquery";
import { fetchPlaceSuggestions, fetchPlaceDetails } from "../utils/placesAutoCompleteService";
import loader from "../utils/googleMapsLoader";
import "../../assets/stylesheets/adwerx/places-autocomplete.scss";

async function ensureGoogleMapsLoaded() {
  // Always use the official loader for the new API
  return loader.load();
}

// Legacy class removed - using NewPlacesAutoComplete as default

class NewPlacesAutoComplete {
  static async initializeUsAddress(selector, autoFocus) {
    return this.initializeAddress(selector, autoFocus, "US");
  }

  static async initializeAddress(selector, autoFocus, country) {
    const mapOpts = {
      componentRestrictions: { country: country || "US" }
    };
    return this.initialize(selector, mapOpts, autoFocus, false);
  }

  static async initializeUsCityState(selector, autoFocus) {
    return this.initializeCityState(selector, autoFocus, "US");
  }

  static async initializeCityState(selector, autoFocus, country) {
    const mapOpts = {
      componentRestrictions: { country: country || "US" }
    };
    return this.initialize(selector, mapOpts, autoFocus, true);
  }

  static async initialize(selector, initMapOpts = {}, autoFocus, citiesOnly = true) {
    await ensureGoogleMapsLoaded();

    const $inputs = $(selector);
    const autocompleteObjects = [];

    $inputs.each((_, input) => {
      const $input = $(input);
      const { form } = input;

      if (form) {
        const hiddenFields = this._setupHiddenFields(form);
        const isListingSearch = $input.hasClass("listings-search-form-address");
        let highlightedIndex = -1;

        // Create dropdown container
        const $dropdown = $("<div>", {
          class: "pac-container--custom",
          css: {
            display: "none"
          }
        });

        // Position the dropdown container
        const positionDropdown = () => {
          const inputPos = $input[0].getBoundingClientRect();
          const { scrollTop } = document.documentElement;
          const { scrollLeft } = document.documentElement;

          $dropdown.css({
            top: `${inputPos.bottom + scrollTop}px`,
            left: `${inputPos.left + scrollLeft}px`,
            width: `${inputPos.width}px`
          });
        };

        // Add the dropdown to the body and position it
        $("body").append($dropdown);
        positionDropdown();

        // Update position on window resize for mobile
        $(window).on("resize", positionDropdown);

        // Helper function to update highlighted item
        const updateHighlight = () => {
          $dropdown.find('.pac-item').removeClass('pac-item--highlighted');
          if (highlightedIndex >= 0) {
            $dropdown.find('.pac-item').eq(highlightedIndex).addClass('pac-item--highlighted');
          }
        };

        // Helper function to select a suggestion
        const selectSuggestion = async (suggestion, displayText) => {
          try {
            // Use the new Place API service to fetch details
            const placeDetails = await fetchPlaceDetails(suggestion.placeId);

            // Update hidden fields
            this._updateHiddenFields(hiddenFields, placeDetails);

            $input.val(displayText).trigger("change");
            $dropdown.hide();
            highlightedIndex = -1;

            if (initMapOpts.submitOnSelect) {
              setTimeout(() => form.submit(), 500);
            }
          } catch (error) {
            console.error("Error handling selection:", error);
          }
        };

        // Add keyboard event listener
        $input.on("keydown", async event => {
          const $items = $dropdown.find('.pac-item');
          const itemCount = $items.length;

          if (!$dropdown.is(':visible') || itemCount === 0) return;

          switch (event.key) {
            case "ArrowDown":
              event.preventDefault();
              highlightedIndex = highlightedIndex < itemCount - 1 ? highlightedIndex + 1 : 0;
              updateHighlight();
              break;
            case "ArrowUp":
              event.preventDefault();
              highlightedIndex = highlightedIndex > 0 ? highlightedIndex - 1 : itemCount - 1;
              updateHighlight();
              break;
            case "Enter":
              event.preventDefault();
              if (highlightedIndex >= 0 && highlightedIndex < itemCount) {
                const $selectedItem = $items.eq(highlightedIndex);
                const suggestion = $selectedItem.data('suggestion');
                const displayText = $selectedItem.data('displayText');
                if (suggestion && displayText) {
                  await selectSuggestion(suggestion, displayText);
                }
              }
              break;
            case "Escape":
              event.preventDefault();
              $dropdown.hide();
              highlightedIndex = -1;
              break;
            default:
              break;
          }
        });

        // Add input event listener
        let debounceTimeout;
        $input.on("input", async event => {
          const value = event.target.value.trim();
          highlightedIndex = -1; // Reset highlighted index on new input

          if (value.length < 2) {
            $dropdown.hide();
            return;
          }

          clearTimeout(debounceTimeout);
          debounceTimeout = setTimeout(async () => {
            try {
              // Use the new service for fetching suggestions
              const suggestions = await fetchPlaceSuggestions(value, {
                country: initMapOpts.componentRestrictions?.country || "US",
                citiesOnly: citiesOnly && !isListingSearch
              });

              $dropdown.empty();

              if (suggestions.length > 0) {
                suggestions.forEach((suggestion, index) => {
                  const mainText = suggestion.mainText?.toString?.() || suggestion.mainText || "";
                  const secondaryText =
                    suggestion.secondaryText?.toString?.() || suggestion.secondaryText || "";

                  let displayText;
                  if (isListingSearch) {
                    displayText = [mainText, secondaryText].filter(Boolean).join(", ");
                  } else {
                    displayText = secondaryText ? `${mainText}, ${secondaryText}` : mainText;
                  }

                  const $item = $("<div>", {
                    class: "pac-item",
                    html: `<span class="pac-text">${displayText}</span>`
                  });

                  // Store suggestion data for keyboard navigation
                  $item.data('suggestion', suggestion);
                  $item.data('displayText', displayText);

                  $item.on("mousedown", async e => {
                    e.preventDefault();
                    e.stopPropagation();
                    await selectSuggestion(suggestion, displayText);
                  });

                  // Add mouse hover to update highlighted index
                  $item.on("mouseenter", () => {
                    highlightedIndex = index;
                    updateHighlight();
                  });

                  $dropdown.append($item);
                });

                positionDropdown();
                $dropdown.show();
                highlightedIndex = -1; // Reset highlight when showing new suggestions
              } else {
                $dropdown.hide();
              }
            } catch (error) {
              console.error("Error fetching suggestions:", error);
              $dropdown.empty().hide();
            }
          }, 300);
        });

        // Handle document click to hide dropdown
        $(document).on("click", event => {
          if (
            !$(event.target).closest($input).length &&
            !$(event.target).closest($dropdown).length
          ) {
            $dropdown.hide();
            highlightedIndex = -1;
          }
        });

        if (autoFocus) {
          setTimeout(() => input.focus(), 200);
        }

        autocompleteObjects.push({ input, dropdown: $dropdown });
      }
    });

    return autocompleteObjects;
  }

  static _setupHiddenFields(form) {
    const hiddenFields = {};
    const fields = [
      "street_number",
      "route",
      "locality",
      "administrative_area_level_1",
      "country",
      "postal_code",
      "latitude",
      "longitude",
      "mls_number",
      "formatted_address",
      "street_name",
      "city",
      "state",
      "zip",
      "country_name",
      "country_iso"
    ];

    fields.forEach(field => {
      const $field = $(`input[data-geo="${field}"]`, form);
      if ($field.length === 0) {
        const $hidden = $("<input>", {
          type: "hidden",
          "data-geo": field,
          name: field
        });
        $(form).append($hidden);
        hiddenFields[field] = $hidden;
      } else {
        hiddenFields[field] = $field;
      }
    });

    return hiddenFields;
  }

  static _updateHiddenFields(hiddenFields, place) {
    if (!place) return;

    // Clear old values
    Object.values(hiddenFields).forEach($field => $field.val(""));

    if (place.location) {
      const { lat, lng } = place.location;
      if (hiddenFields.latitude) hiddenFields.latitude.val(lat);
      if (hiddenFields.longitude) hiddenFields.longitude.val(lng);
    }

    // Set formatted_address
    if (hiddenFields.formatted_address && place.formattedAddress) {
      hiddenFields.formatted_address.val(place.formattedAddress);
    }

    // Parse address components
    if (place.addressComponents) {
      place.addressComponents.forEach(component => {
        switch (component.type) {
          case "street_number":
            if (hiddenFields.street_number) hiddenFields.street_number.val(component.shortName);
            break;
          case "route":
            if (hiddenFields.street_name) hiddenFields.street_name.val(component.shortName);
            break;
          case "locality":
            if (hiddenFields.city) hiddenFields.city.val(component.shortName);
            break;
          case "administrative_area_level_1":
            if (hiddenFields.state) hiddenFields.state.val(component.shortName);
            break;
          case "postal_code":
            if (hiddenFields.zip) hiddenFields.zip.val(component.shortName);
            break;
          case "country":
            if (hiddenFields.country_name)
              hiddenFields.country_name.val(component.shortNameLong || component.shortName);
            if (hiddenFields.country_iso) hiddenFields.country_iso.val(component.shortName);
            break;
          default:
            // No action needed for other types
            break;
        }
      });
    }
  }
}

// Export NewPlacesAutoComplete as default (legacy removed)
export default NewPlacesAutoComplete;
